import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { X } from "lucide-react";

export default function TooltipOverlay({ screen, targetSelector, message, placement = "bottom" }) {
  const { user } = useAuth();
  const { tourRunning } = useOnboarding();
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const dismissed = useRef(false);

  useEffect(() => {
    if (!user || tourRunning || dismissed.current) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/tour-state`);
        const seen = data.tooltips_seen || [];
        if (!seen.includes(screen) && !cancelled) setVisible(true);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, screen, tourRunning]);

  useEffect(() => {
    if (!visible || tourRunning) { setPos(null); return; }
    const position = () => {
      const el = document.querySelector(targetSelector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const p = placement === "top"
        ? { top: r.top - 8, left: r.left + r.width / 2, anchor: "bottom" }
        : { top: r.bottom + 8, left: r.left + r.width / 2, anchor: "top" };
      setPos(p);
    };
    position();
    const id = setInterval(position, 500);
    return () => clearInterval(id);
  }, [visible, targetSelector, placement, tourRunning]);

  const dismiss = async () => {
    dismissed.current = true;
    setVisible(false);
    try { await axios.post(`${API}/auth/tooltip-seen`, { screen }); } catch {}
  };

  if (!visible || !pos || tourRunning) return null;

  const transform = pos.anchor === "bottom" ? "translate(-50%, -100%)" : "translate(-50%, 0)";
  const arrowStyle = pos.anchor === "bottom"
    ? { bottom: -6, left: "50%", transform: "translateX(-50%) rotate(45deg)" }
    : { top: -6, left: "50%", transform: "translateX(-50%) rotate(45deg)" };

  return createPortal(
    <div
      onClick={dismiss}
      className="fixed inset-0 z-[9999]"
      data-testid={`tooltip-overlay-${screen}`}
    >
      <div
        className="absolute bg-slate-800 text-white rounded-xl px-3.5 py-2.5 shadow-2xl max-w-[260px]"
        style={{ top: pos.top, left: pos.left, transform }}
        onClick={e => e.stopPropagation()}
        data-testid={`tooltip-card-${screen}`}
      >
        <div className="absolute w-3 h-3 bg-slate-800 rounded-sm" style={arrowStyle} />
        <div className="flex items-start gap-2 relative">
          <p className="text-xs leading-relaxed flex-1">{message}</p>
          <button onClick={dismiss} className="p-0.5 rounded-full hover:bg-white/10 flex-shrink-0 -mt-0.5" data-testid={`tooltip-dismiss-${screen}`}>
            <X className="w-3.5 h-3.5 text-white/60" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
